import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { useOrganization } from "@/auth/organization";
import {
  Button,
  ErrorAlert,
  EmptyState,
  Field,
  Form,
  Input,
  Modal,
  Section,
  useToast,
  type Requirement,
} from "@/components/ui";
import { RuleRow } from "@/components/domains/parts";
import { domainKeys, domainsApi, has, type ChildNameserver, type DomainDetail } from "@/lib/domains";

interface Draft {
  id: string | null;
  hostname: string;
  ipv4: string;
  ipv6: string;
}

function draftFor(c: ChildNameserver | null): Draft {
  return c
    ? { id: c.id, hostname: c.hostname, ipv4: c.ipv4, ipv6: c.ipv6 ?? "" }
    : { id: null, hostname: "", ipv4: "", ipv6: "" };
}

/**
 * Child nameservers (PHP-3942, AC2-3): hostnames under this domain registered as nameservers at the
 * registry — set up regardless of `nameserver_mode`, unlike everything else this ticket adds.
 */
export function ChildNameserversSection({ detail }: { detail: DomainDetail }) {
  const d = detail.domain;
  const { orgId, canOn } = useOrganization();
  const qc = useQueryClient();
  const toast = useToast();
  const manage: Requirement = { permission: "domains:manage", on: ["domain_folder", detail.folder?.id] };
  const canManage = canOn(manage.permission, "domain_folder", detail.folder?.id);

  const listKey = [...domainKeys.all(orgId), "child-nameservers", d.id];
  const list = useQuery({
    queryKey: listKey,
    queryFn: () => domainsApi.childNameservers(orgId, d.id),
    enabled: has(detail, "child_nameservers"),
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<ChildNameserver | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: domainKeys.all(orgId) });

  const create = useMutation({
    mutationFn: (body: { hostname: string; ipv4: string; ipv6: string | null }) =>
      domainsApi.createChildNameserver(orgId, d.id, body),
    onSuccess: async () => {
      await invalidate();
      toast.show({ tone: "success", title: "Child nameserver added" });
      setDraft(null);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, ipv4, ipv6 }: { id: string; ipv4: string; ipv6: string | null }) =>
      domainsApi.updateChildNameserver(orgId, d.id, id, { ipv4, ipv6 }),
    onSuccess: async () => {
      await invalidate();
      toast.show({ tone: "success", title: "Child nameserver updated" });
      setDraft(null);
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => domainsApi.deleteChildNameserver(orgId, d.id, id),
    onSuccess: async () => {
      await invalidate();
      toast.show({ tone: "success", title: "Child nameserver deleted" });
      setRemoving(null);
    },
  });

  if (!has(detail, "child_nameservers")) return null;

  const closeDraft = () => {
    if (create.isPending || update.isPending) return;
    setDraft(null);
    create.reset();
    update.reset();
  };
  const submit = () => {
    if (!draft) return;
    const ipv6 = draft.ipv6.trim() || null;
    if (draft.id) update.mutate({ id: draft.id, ipv4: draft.ipv4, ipv6 });
    else create.mutate({ hostname: draft.hostname, ipv4: draft.ipv4, ipv6 });
  };

  return (
    <Section
      title="Child nameservers"
      subtitle="Hostnames under this domain, registered as nameservers at the registry"
      action={
        canManage && (
          <Button size="lg" variant="secondary" requires={manage} onClick={() => setDraft(draftFor(null))}>
            Add child nameserver
          </Button>
        )
      }
    >
      {list.isPending ? (
        <p className="text-[13px] text-fg-subtle">Loading…</p>
      ) : list.isError ? (
        <ErrorAlert error={list.error} />
      ) : list.data.nameservers.length === 0 ? (
        <EmptyState
          title="No child nameservers yet"
          description={`Add hostnames like ns1.${d.display_name} to use as this domain's own nameservers at the registry.`}
          action={
            canManage && (
              <Button variant="secondary" onClick={() => setDraft(draftFor(null))}>
                Add child nameserver
              </Button>
            )
          }
        />
      ) : (
        list.data.nameservers.map((c, i) => (
          <RuleRow key={c.id} first={i === 0}>
            <div className="min-w-0 flex-1">
              <p className="text-sm break-all text-fg">{c.hostname}</p>
              <p className="text-xs break-all text-fg-subtle">
                {c.ipv4}
                {c.ipv6 ? ` · ${c.ipv6}` : ""}
              </p>
            </div>
            {canManage && (
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="secondary" requires={manage} onClick={() => setDraft(draftFor(c))}>
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-[#b52522] hover:text-[#b52522]"
                  requires={manage}
                  onClick={() => setRemoving(c)}
                >
                  Delete
                </Button>
              </div>
            )}
          </RuleRow>
        ))
      )}

      {draft && (
        <Modal
          open
          onClose={closeDraft}
          title={draft.id ? "Edit child nameserver" : "Add child nameserver"}
          footer={
            <>
              <Button variant="secondary" size="lg" onClick={closeDraft}>
                Cancel
              </Button>
              <Button
                variant="brand"
                size="lg"
                type="submit"
                form="child-ns-form"
                loading={create.isPending || update.isPending}
              >
                {draft.id ? "Save changes" : "Add nameserver"}
              </Button>
            </>
          }
        >
          <Form
            id="child-ns-form"
            className="flex flex-col gap-4"
            error={create.error ?? update.error}
            onSubmit={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {draft.id ? (
              <div>
                <p className="text-[13px] font-semibold text-[#5c5551]">Hostname</p>
                <p className="text-sm text-fg">{draft.hostname}</p>
              </div>
            ) : (
              <Field label="Hostname" name="hostname" required>
                {(control) => (
                  <Input
                    {...control}
                    value={draft.hostname}
                    autoComplete="off"
                    spellCheck={false}
                    placeholder={`ns1.${d.display_name}`}
                    onChange={(e) => setDraft({ ...draft, hostname: e.target.value })}
                  />
                )}
              </Field>
            )}
            <Field label="IPv4 address" name="ipv4" required>
              {(control) => (
                <Input
                  {...control}
                  value={draft.ipv4}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="76.223.100.4"
                  onChange={(e) => setDraft({ ...draft, ipv4: e.target.value })}
                />
              )}
            </Field>
            <Field label="IPv6 address" name="ipv6" optional>
              {(control) => (
                <Input
                  {...control}
                  value={draft.ipv6}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="2001:db8::1"
                  onChange={(e) => setDraft({ ...draft, ipv6: e.target.value })}
                />
              )}
            </Field>
            <ErrorAlert error={create.error ?? update.error} />
          </Form>
        </Modal>
      )}

      {removing && (
        <Modal
          open
          onClose={() => {
            if (remove.isPending) return;
            setRemoving(null);
            remove.reset();
          }}
          width={420}
          title="Delete this child nameserver?"
          note="This can't be undone."
          footer={
            <>
              <Button variant="secondary" size="lg" onClick={() => setRemoving(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="lg"
                loading={remove.isPending}
                onClick={() => remove.mutate(removing.id)}
              >
                Delete nameserver
              </Button>
            </>
          }
        >
          <p className="text-[13px] text-[#5c5551]">
            {removing.hostname} ({removing.ipv4}) will be removed from the registry.
          </p>
          <ErrorAlert error={remove.error} />
        </Modal>
      )}
    </Section>
  );
}
